import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import * as actions from '@actions';
import { GPPracticesSchema } from '../../../../store/actions/patients';

export type GPPractice = z.infer<typeof GPPracticesSchema>['practices'][0];

export interface GPPracticeFormatted {
  label: string;
  address: {
    gp_email?: string;
    gp_ods_code: string;
    gp_practice_name: string;
    gp_postcode: string;
  };
  ods_code: string;
  docman_active: boolean;
  gp_postcode: string;
  raw: GPPractice;
}

const fetchGPPractices = async (
  postcode: string,
  dispatch: ReturnType<typeof useDispatch>
): Promise<GPPracticeFormatted[]> => {
  if (postcode.trim().length === 0) {
    throw new Error('Please enter a postcode to search for');
  }

  const response = await dispatch(actions.fetchGPs(postcode), false);

  if (response instanceof Response && response.status >= 400) {
    throw new Error(response.statusText);
  }

  if (!response.practices || response.practices.length === 0) {
    throw new Error('No GP practices found for this postcode');
  }

  return response.practices.map((practice) => {
    const { practice_name, addline1, addline2, addline3, addline4, region, email, ods_code, postcode, docman_active } =
      practice;

    const label = [practice_name, addline1, addline2, addline3, addline4, region, postcode].filter(Boolean).join(', ');

    return {
      label,
      address: {
        gp_email: email || undefined,
        gp_ods_code: ods_code,
        gp_practice_name: practice_name,
        gp_postcode: postcode
      },
      ods_code,
      docman_active,
      gp_postcode: postcode,
      raw: practice
    };
  });
};

export const useGPPracticesSearch = (postcode: string) => {
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ['gp-practices', postcode],
    queryFn: () => fetchGPPractices(postcode, dispatch),
    enabled: !!postcode,
    staleTime: 10 * 60 * 1000
  });
};
